import * as React from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { getFeedbackList, addComment } from '@feedback/services/index'
import { Button, Icon, TextareaItem, Toast } from 'antd-mobile'
import LeaveWarningModal from '@feedback/components/LeaveWarningModal'
import { getTextByKey, reportFn, getTimeFormat } from '@feedback/utils/util'
import { getUA } from '@/utils/utils'
import { appUploadAddress } from '@feedback/utils/url'
import { queryClient } from '@feedback/pages/allfb/App'
import './index.scss'
import { getAppConfig } from '@feedback/utils/CONST'
import { isVestAppid } from '@feedback/utils/siteConfig'
import VestLayout from '@feedback/pages/allfb/vest/Layout'
import clsx from 'clsx'

const statusSet: {
  [key: string]: { text: string; icon: string }
} = {
  1: { text: getTextByKey('processing'), icon: '' },
  2: { text: getTextByKey('wait for your reply'), icon: '' },
  3: { text: getTextByKey('processing'), icon: '' },
  4: { text: getTextByKey('solved'), icon: '' },
  5: { text: getTextByKey('solved'), icon: '' },
  6: { text: getTextByKey('solved'), icon: '' }
}

enum tabStatus {
  processing = 1,
  resolved = 2
}


export const submitComment = async (params: any) => {
  const { code, data, message } = (await addComment(params)) || {}
  if (code !== 0) {
    return Promise.reject(new Error(message))
  }
  return data
}

export const FeedbackSubmit: React.FC<
  {
    onTextAreaScroll?: React.UIEventHandler<HTMLInputElement>
    alert?: (value: any) => void
    tabActive?: tabStatus
  } & IFeedback
> = ({
  tabActive,
  feedback_status,
  _id,
  star,
  comment,
  onTextAreaScroll,
  alert
}) => {
  const [isCollapse, setCollapse] = React.useState(false)
  const [form, setForm] = React.useState({ star, comment })
  // O1：提交成功到列表 refetch 之间，本地标记已评，避免闪回 Rate
  const [ratedLocal, setRatedLocal] = React.useState(false)
  const { appid } = getPageConfig()
  const isVest = isVestAppid(appid)
  const isRated = Number(star) > 0 || ratedLocal

  const pushComment = useMutation(submitComment, {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries('getFeedbackList')
      setCollapse(false)
      if (isVest) {
        setRatedLocal(true)
        // 原型口径：评分后仅 toast 致谢，无 App 商店评分引导弹窗
        Toast.info(getTextByKey('thank rate'), 2)
        return
      }
      if (variables.star === 5) {
        alert?.({
          open: true,
          title: getTextByKey('notification'),
          message: getTextByKey('satisfaction modal tip'),
          onCancel: () => true,
          confirmText: getTextByKey('confirm'),
          onConfirm: () => {
            reportFn({ parm1: 4, parm2: 3, parm6: variables.feedback_id })
            const UA = getUA()
            if (UA?.ios) {
              appUploadAddress?.[appid]?.ios &&
                window.open(appUploadAddress?.[appid]?.ios)
            } else {
              appUploadAddress?.[appid]?.ios &&
                window.open(appUploadAddress?.[appid]?.android)
            }
            return true
          }
        })
      } else {
        alert?.({
          open: true,
          title: getTextByKey('notification'),
          message: getTextByKey('thank rate'),
          confirmText: getTextByKey('confirm'),
          onConfirm: () => {
            return true
          }
        })
      }
    },
    onError: ({ message }) => {
      message && Toast.info(`${message}`)
    }
  })

  // 马甲站 V1：已评显示灰色「已评价」标签（产品要求可见状态；相对原型有意偏离）
  // 无 stopPropagation：点灰字随卡片进 ticketDetail
  if (isVest && isRated) {
    return (
      <div className='ticket-list-feedback__footer'>
        <div className='ticket-list-feedback__footer--rated'>
          {getTextByKey('Rated')}
        </div>
      </div>
    )
  }

  // 马甲站：评分走底部弹层（原型 history-rating-sheet），
  // 表情 1–5 分单选 + 反馈输入 + 通栏品牌色 Submit
  if (isVest) {
    return (
      <div
        className='ticket-list-feedback__footer'
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
        }}
      >
        <div
          className='ticket-list-feedback__footer--evaluate'
          onClick={() => {
            reportFn({ parm1: 4, parm2: 1, parm6: _id })
            setCollapse(true)
          }}
        >
          {getTextByKey('rate our service')}
        </div>
        {isCollapse && (
          <div className='vest-rating-layer'>
            <button
              type='button'
              className='vest-rating-layer__mask'
              aria-label='Close service rating'
              onClick={() => setCollapse(false)}
            />
            <section className='vest-rating-layer__sheet'>
              <header className='vest-rating-layer__header'>
                <h2>{getTextByKey('Are you satisfied with our service')}</h2>
                <button
                  type='button'
                  className='vest-rating-layer__close'
                  aria-label='Close'
                  onClick={() => setCollapse(false)}
                />
              </header>
              <div className='vest-rating-layer__icons'>
                {Array.from(Array(5)).map((_, index) => (
                  <button
                    type='button'
                    key={index}
                    className={clsx(
                      'vest-rating-layer__icon',
                      index + 1 <= Number(form.star) && 'is-active'
                    )}
                    aria-label={`Rating ${index + 1}`}
                    aria-pressed={Number(form.star) === index + 1}
                    onClick={() =>
                      setForm((val) => ({
                        ...val,
                        star: Number(val.star) === index + 1 ? 0 : index + 1
                      }))
                    }
                  >
                    <i aria-hidden='true' />
                  </button>
                ))}
              </div>
              <textarea
                className='vest-rating-layer__input'
                placeholder={getTextByKey('Please input your feedback')}
                value={form.comment || ''}
                onChange={(event) => {
                  const value = event.currentTarget.value
                  setForm((val) => ({ ...val, comment: value }))
                }}
              />
              <button
                type='button'
                className='vest-rating-layer__submit'
                disabled={!Number(form.star) || pushComment.isLoading}
                onClick={() => {
                  reportFn({ parm1: 4, parm2: 2, parm6: _id })
                  pushComment.mutate({ ...form, feedback_id: _id })
                }}
              >
                {getTextByKey('submit')}
              </button>
            </section>
          </div>
        )}
      </div>
    )
  }

  if (comment && star) {
    return (
      <div className='ticket-list-feedback__footer'>
        <div className='ticket-list-feedback__star'>
          {Array.from(Array(5)).map((_, index) => (
            <i className={clsx(index + 1 <= star && 'is-active')} key={index} />
          ))}
        </div>
        {comment && (
          <p className='ticket-list-feedback__footer--content'>{comment}</p>
        )}
      </div>
    )
  }

  return (
    <div
      className='ticket-list-feedback__footer'
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
    >
      {!isCollapse && (
        <div
          className={clsx('ticket-list-feedback__footer--evaluate')}
          onClick={() => {
            reportFn({ parm1: 4, parm2: 1, parm6: _id })
            setCollapse(true)
          }}
        >
          {getTextByKey('rate our service')}
        </div>
      )}
      {isCollapse && (
        <>
          <div className='ticket-list-feedback__star'>
            {Array.from(Array(5)).map((_, index) => (
              <i
                className={clsx(index + 1 <= form.star && 'is-active')}
                key={index}
                onClick={() => {
                  if (Number(star)) {
                    return
                  }

                  if (Number(form.star) === index + 1) {
                    setForm((val) => ({ ...val, star: 0 }))
                    return
                  }

                  setForm((val) => ({ ...val, star: index + 1 }))
                }}
              />
            ))}
          </div>
          <div className='ticket-list-feedback__textarea'>
            <TextareaItem
              onScroll={onTextAreaScroll}
              placeholder={getTextByKey('Are you satisfied with our service')}
              value={form.comment}
              onChange={(value) =>
                setForm({ ...form, comment: value as string })
              }
              rows={4}
            />
          </div>
          {Number(form.star) ? (
            <Button
              loading={pushComment.isLoading}
              disabled={pushComment.isLoading}
              className={clsx('ticket-list-feedback__submit')}
              onClick={() => {
                reportFn({ parm1: 4, parm2: 2, parm6: _id })
                pushComment.mutate({ ...form, feedback_id: _id })
                // getTextByKey('Whether to submit the current feedback')
              }}
            >
              {getTextByKey('submit')}
              <i className='icon' />
            </Button>
          ) : (
            <div
              className='ticket-list-feedback__pack-up'
              onClick={() => {
                setCollapse(false)
              }}
            >
              {getTextByKey('collapse')}
              <i className='icon' />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export interface IFeedback {
  comment: string
  content: string
  created_at: string
  feedback_status: number
  is_comment: number
  question_id: number
  qus_id: number
  star: number
  title: string
  work_order: string
  _id: string
  red?: number
}

export const fetchFeedbackList = async ({
  pageParam: page = 1,
  limit = 100,
  status
}: {
  pageParam?: number
  limit?: number
  status?: tabStatus
}): Promise<{
  list: IFeedback[]
  total: number
}> => {
  const { code, data, message } =
    (await getFeedbackList({
      page,
      limit,
      status
    })) || {}
  if (code !== 0) {
    return Promise.reject(new Error(message))
  }
  const { list, count } = data || {}
  return {
    list: Array.isArray(list) ? list : [],
    total: count
  }
}

export const FeedbackList: React.FC<{
  status: tabStatus
  alert?: (value: any) => void
}> = ({ status, alert }) => {
  const feedback = useQuery<{ list: IFeedback[]; total: number }, Error>(
    ['getFeedbackList', status],
    ({ pageParam }) => fetchFeedbackList({ pageParam, status })
  )

  if (feedback.isLoading) {
    return (
      <div className={'faq-list__more'}>
        <Icon type='loading' />
      </div>
    )
  }

  if (feedback.isError) {
    return <div className={'faq-list__more'}>{feedback?.error?.message}</div>
  }

  return (
    <div className='ticket-list-feedback'>
      {feedback.data?.list.map((item) => (
        <div
          className={`ticket-list-feedback__item ${
            Number(item.red) === 1 ? 'is-red-dot' : ''
          }`}
          key={item._id}
        >
          <Link
            className='ticket-list-feedback__item-link'
            to={`/ticketDetail?feedback_id=${item._id}`}
          >
            <div className='ticket-list-feedback__header'>
              <div className='id'>ID: {item.work_order}</div>
              <div className='time'>{getTimeFormat(item.created_at)}</div>
            </div>
            <div className='ticket-list-feedback__content'>
              <p>{item.content}</p>
            </div>
          </Link>
          {status === tabStatus.resolved && statusSet[item.feedback_status] && (
            <FeedbackSubmit {...item} tabActive={status} alert={alert} />
          )}
        </div>
      ))}

      <div className={`faq-list__more`}>
        {getTextByKey('Nothing more to load')}
      </div>
    </div>
  )
}

const getPageConfig = () => {
  const { appid, logo, logoShadow, bgSearch } = getAppConfig() || {}
  return {
    ...(appid === 'cm1001' && { logo }),
    ...(appid === 'cm1002' && { logo }),
    ...(appid === 'cm1003' && { logo }),
    ...(appid === 'cm1006' && { logo }),
    ...(appid === 'cm1007' && { logo }),
    ...(appid === 'cm1008' && { logo: logoShadow }),
    ...(appid === 'cm1009' && { logo }),
    ...(appid === 'cm1017' && { logo }),
    ...(appid === 'cm1011' && { logo: logoShadow }),
    ...(appid === 'cm1012' && { logo }),
    ...(appid === 'cm1015' && { logo }),
    ...(appid === 'cm3002' && { logo }),
    ...(appid === 'cm1013' && { logo }),
    ...(appid === 'cm1018' && { logo }),
    appid,
    bgSearch
  }
}

const TicketList: React.FC = () => {
  const history = useHistory()
  const { appid, logo, bgSearch } = getPageConfig()
  const isVest = isVestAppid(appid)
  const { status: status_index } = Object.fromEntries(
    new URLSearchParams(window.location.href.split('?')[1])
  )

  const [status, setStatus] = React.useState(
    Number(status_index) ||
      Number(window.sessionStorage.getItem('tabStatus')) ||
      tabStatus.processing
  )

  const [modal, setModal] = React.useState({
    open: false,
    message: '',
    cancelText: '',
    onCancel: () => {},
    confirmText: '',
    onConfirm: () => {}
  })

  const alert = (value: any) => {
    setModal({
      ...value,
      onCancel: value.onCancel
        ? () => {
            if (value?.onCancel()) {
              setModal((val) => ({ ...val, open: false }))
            }
            return false
          }
        : false,
      onConfirm: value.onConfirm
        ? () => {
            if (value?.onConfirm()) {
              setModal((val) => ({ ...val, open: false }))
            }
            return false
          }
        : false
    })
  }

  React.useEffect(() => {
    window.sessionStorage.setItem('tabStatus', status.toString())
  }, [status])

  const tabs = (
    <div className='ticket-list-tab' role='tablist'>
      <div
        role='tab'
        aria-selected={status === tabStatus.processing}
        className={clsx(status === tabStatus.processing && 'is-active')}
        onClick={() => setStatus(tabStatus.processing)}
      >
        {getTextByKey('processing')}
      </div>
      <div
        role='tab'
        aria-selected={status === tabStatus.resolved}
        className={clsx(status === tabStatus.resolved && 'is-active')}
        onClick={() => setStatus(tabStatus.resolved)}
      >
        {getTextByKey('solved')}
      </div>
    </div>
  )

  if (isVest) {
    return (
      <>
        <VestLayout
          showBottomBar={false}
          title={getTextByKey('feedback history')}
          // H1：任意入口进 History，‹ 一律回反馈首页（禁止 goBack 多入口迷路）
          onBack={() => {
            history.push('/')
          }}
          className='vest-history-shell'
          bodyClassName='vest-history-body'
        >
          <div className={clsx('ticket-list', 'is-vest', appid)}>
            {tabs}
            <FeedbackList status={status} alert={alert} />
          </div>
        </VestLayout>
        <LeaveWarningModal {...modal} />
      </>
    )
  }

  return (
    <>
      <div className={clsx('ticket-list', appid)}>
        <header className='ticket-list-header'>
          <div
            className='ticket-list-header__bg-search'
            style={{ backgroundImage: `url(${bgSearch})` }}
          />
          <div className={clsx('ticket-list-header__title')}>
            {getTextByKey('feedback history')}
          </div>
          <Link to='/'>
            <div
              className='ticket-list-header__logo'
              style={{ backgroundImage: `url(${logo})` }}
            />
          </Link>
          {tabs}

          {['cm1006', 'cm1007']?.includes(appid) && (
            <div className='round-b'></div>
          )}
        </header>

        <FeedbackList status={status} alert={alert} />
      </div>

      <LeaveWarningModal {...modal} />
    </>
  )
}

export default TicketList
